import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Edit02Icon, FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded/index'
import { Cancel01Icon } from '@hugeicons-pro/core-twotone-rounded/index'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useUpdateOrderMutation } from '@/app/services/orders'
import { selectOrganisationId } from '@/app/slices/organisationSlice'

import SaveNotesDialog from '../common/SaveNotesDialog'

const classes = {
    icon: {
        fontSize: '16px',
    },
}

const OrderNotesModal = ({ onClose, open, order }) => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const { enqueueSnackbar } = useSnackbar()

    const organisationId = useSelector(selectOrganisationId)
    const [updateOrder] = useUpdateOrderMutation()

    const [isEditingCustomerNotes, setIsEditingCustomerNotes] = useState(false)
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

    const [customerNotes, setCustomerNotes] = useState(order?.customerNotes)

    const handleUpdateOrder = async (newStuff) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    id: orderId,
                    organisationId: organisationId,
                    ...newStuff,
                },
            }).unwrap()
        } catch (error) {
            const errorMessage = t('$t(An error occurred) updating order notes.')
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const toggleEditCustomerNotes = () => {
        if (isEditingCustomerNotes) {
            handleUpdateOrder({ ...order, customerNotes })
        }
        setIsEditingCustomerNotes((v) => !v)
    }

    const handleCustomerNotesChange = (event) => {
        const { value } = event.target
        if (value != customerNotes) {
            setCustomerNotes(value)
        }
    }

    const handleClose = (_, reason) => {
        if (reason !== 'backdropClick') {
            if (isEditingCustomerNotes) {
                setShowConfirmationDialog(true)
            } else {
                onClose()
            }
        }
    }

    const onSaveNotesDialogCancel = () => {
        setShowConfirmationDialog(false)
        onClose()
    }

    const onSaveNotesDialogConfirm = () => {
        setShowConfirmationDialog(false)
        handleUpdateOrder({
            ...order,

            customerNotes,
        })
        onClose()
    }

    useEffect(() => {
        setCustomerNotes(order?.customerNotes)

        return () => {
            setCustomerNotes('')
        }
    }, [order])

    return (
        <Dialog
            open={open}
            PaperProps={{ sx: { width: 400 } }}
            disableEscapeKeyDown
            onClose={handleClose}
        >
            <DialogTitle
                alignItems="center"
                data-testid="order-notes-modal"
                display="flex"
                justifyContent="space-between"
            >
                <Typography variant="strong1">{`${t('NOTES FOR ORDER')} ${order?.name}`}</Typography>
                <IconButton
                    color="secondary"
                    size="small"
                    onClick={handleClose}
                >
                    <HugeiconsIcon icon={Cancel01Icon} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                data-testid="order-customer-notes-label"
                                variant="strong2"
                            >
                                {t('Customer notes')}
                            </Typography>
                            <IconButton
                                color="secondary"
                                size="small"
                                onClick={toggleEditCustomerNotes}
                            >
                                <HugeiconsIcon
                                    altIcon={FloppyDiskIcon}
                                    icon={Edit02Icon}
                                    showAlt={isEditingCustomerNotes}
                                    size={18}
                                />
                            </IconButton>
                        </Box>
                        <TextField
                            color="secondary"
                            data-testid="order-customer-notes"
                            helperText={t('Your notes about this order.')}
                            placeholder={t('Optionally add notes about this order')}
                            rows={4}
                            slotProps={{
                                input: {
                                    readOnly: !isEditingCustomerNotes,
                                    style: { fontSize: 14 },
                                },
                            }}
                            value={customerNotes}
                            fullWidth
                            multiline
                            onChange={handleCustomerNotesChange}
                        />
                    </Box>

                    {order?.vendorNotes ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        data-testid="order-public-notes-label"
                                        variant="strong2"
                                    >
                                        {t('Vendor notes')}
                                    </Typography>
                                </Box>
                            </Box>
                            <TextField
                                color="secondary"
                                data-testid="order-public-notes"
                                helperText={t('Our notes about this order.')}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        style: { fontSize: 14 },
                                    },
                                }}
                                value={order?.vendorNotes}
                                fullWidth
                                multiline
                            />
                        </Box>
                    ) : null}
                </Box>
            </DialogContent>
            <SaveNotesDialog
                open={showConfirmationDialog}
                onCancel={onSaveNotesDialogCancel}
                onConfirm={onSaveNotesDialogConfirm}
            />
        </Dialog>
    )
}

export default OrderNotesModal

OrderNotesModal.propTypes = {
    open: PropTypes.bool,
    order: PropTypes.object,
    onClose: PropTypes.func,
}
